import type { BulkConversationAction } from "@redotech/effect-api-shared/models/shopper-ai/conversation/bulk-conversation-action";
import { useHandler } from "@redotech/react-util/hook";
import StatusIcon from "@redotech/redo-icons/status.svg";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoButtonDropdown } from "@redotech/redo-web/arbiter-components/buttons/redo-dropdown-button";
import {
  RedoCommandMenu,
  type RedoCommandMenuItem,
} from "@redotech/redo-web/arbiter-components/command-menu/redo-command-menu";
import { Flex } from "@redotech/redo-web/flex";
import ArchiveIcon from "@redotech/redo-web/icon-old/archive.svg";
import EnvelopeOpen from "@redotech/redo-web/icon-old/envelope-open.svg";
import EnvelopeIcon from "@redotech/redo-web/icon-old/mail.svg";
import TagIcon from "@redotech/redo-web/icon-old/tag.svg";
import ThreeDotsHorizontalIcon from "@redotech/redo-web/icon-old/three-dots-horizontal.svg";
import UndoArrow from "@redotech/redo-web/icon-old/undo-arrow.svg";
import UserIcon from "@redotech/redo-web/icon-old/user.svg";
import { memo, useCallback, useState } from "react";
import { useBulkConversationAction } from "../hooks/use-bulk-conversation-action";
import { useConversationsTableContext } from "../table/conversations-table-context";
import {
  BulkAddTagsModal,
  BulkAssignModal,
  BulkRemoveTagsModal,
} from "./bulk-action-modals";

export const BulkConversationActionsBar = memo(
  function BulkConversationActionsBar() {
    const {
      selectedConversationIds,
      selectedConversationCount,
      clearSelectedConversations,
    } = useConversationsTableContext();
    const { pendingAction, startBulkConversationAction } =
      useBulkConversationAction();

    const [closeDropdownOpen, setCloseDropdownOpen] = useState(false);
    const [closeButtonRef, setCloseButtonRef] =
      useState<HTMLButtonElement | null>(null);
    const handleCloseButtonRef = useCallback(
      (ref: HTMLButtonElement) => setCloseButtonRef(ref),
      [],
    );

    const [menuOpen, setMenuOpen] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [addTagsModalOpen, setAddTagsModalOpen] = useState(false);
    const [removeTagsModalOpen, setRemoveTagsModalOpen] = useState(false);

    const startAction = useHandler(async (action: BulkConversationAction) => {
      await startBulkConversationAction({
        action,
        conversationIds: selectedConversationIds,
      });
      clearSelectedConversations();
    });

    const selectedText =
      selectedConversationCount === 1
        ? "1 ticket selected"
        : `${selectedConversationCount} tickets selected`;

    const closeDropdownItems: RedoCommandMenuItem[] = [
      {
        Icon: UndoArrow,
        text: "Reopen",
        onClick: () => {
          setCloseDropdownOpen(false);
          void startAction({ _tag: "setConversationStatus", status: "OPEN" });
        },
        disabled: pendingAction !== null,
      },
      {
        Icon: StatusIcon,
        text: "Mark in progress",
        onClick: () => {
          setCloseDropdownOpen(false);
          void startAction({
            _tag: "setConversationStatus",
            status: "IN_PROGRESS",
          });
        },
        disabled: pendingAction !== null,
      },
      {
        Icon: ArchiveIcon,
        text: "Delete",
        onClick: () => {
          setCloseDropdownOpen(false);
          void startAction({
            _tag: "setConversationStatus",
            status: "ARCHIVED",
          });
        },
        disabled: pendingAction !== null,
      },
    ];

    const menuItems: RedoCommandMenuItem[] = [
      {
        Icon: UserIcon,
        text: "Assign",
        onClick: () => {
          setMenuOpen(false);
          setAssignModalOpen(true);
        },
        disabled: pendingAction !== null,
      },
      {
        Icon: TagIcon,
        text: "Add tags",
        onClick: () => {
          setMenuOpen(false);
          setAddTagsModalOpen(true);
        },
        disabled: pendingAction !== null,
      },
      {
        Icon: TagIcon,
        text: "Remove tags",
        onClick: () => {
          setMenuOpen(false);
          setRemoveTagsModalOpen(true);
        },
        disabled: pendingAction !== null,
      },
      {
        Icon: EnvelopeIcon,
        text: "Mark unread",
        onClick: () =>
          void startAction({ _tag: "setReadStatus", status: "unread" }),
        disabled: pendingAction !== null,
      },
      {
        Icon: EnvelopeOpen,
        text: "Mark read",
        onClick: () =>
          void startAction({ _tag: "setReadStatus", status: "read" }),
        disabled: pendingAction !== null,
      },
    ];

    return (
      <>
        {assignModalOpen && (
          <BulkAssignModal
            onClose={() => setAssignModalOpen(false)}
            onConfirm={(userId) => {
              setAssignModalOpen(false);
              void startAction({ _tag: "setAssignee", userId });
            }}
          />
        )}
        {addTagsModalOpen && (
          <BulkAddTagsModal
            onClose={() => setAddTagsModalOpen(false)}
            onConfirm={(tagIds) => {
              setAddTagsModalOpen(false);
              void startAction({ _tag: "addTags", tagIds });
            }}
          />
        )}
        {removeTagsModalOpen && (
          <BulkRemoveTagsModal
            onClose={() => setRemoveTagsModalOpen(false)}
            onConfirm={(tagIds) => {
              setRemoveTagsModalOpen(false);
              void startAction({ _tag: "removeTags", tagIds });
            }}
          />
        )}
        <Flex align="center" gap="sm">
          <RedoButton
            hierarchy="secondary"
            IconTrailing={XIcon}
            onClick={clearSelectedConversations}
            size="sm"
            text={selectedText}
          />
          <RedoButtonDropdown
            disabled={pendingAction !== null}
            dropdownOpen={closeDropdownOpen}
            hierarchy="primary"
            onClick={() =>
              startAction({ _tag: "setConversationStatus", status: "CLOSED" })
            }
            ref={handleCloseButtonRef}
            refSetToContainer
            setDropdownOpen={setCloseDropdownOpen}
            size="sm"
            text="Close"
            theme="normal"
          >
            <RedoCommandMenu
              anchor={closeButtonRef}
              items={closeDropdownItems}
              open={closeDropdownOpen}
              placement="bottom-end"
              setOpen={setCloseDropdownOpen}
            />
          </RedoButtonDropdown>
          <RedoButton
            hierarchy="secondary"
            IconLeading={ThreeDotsHorizontalIcon}
            onClick={() => setMenuOpen(!menuOpen)}
            ref={setMenuAnchor}
            size="sm"
          />
          <RedoCommandMenu
            anchor={menuAnchor}
            items={menuItems}
            open={menuOpen}
            setOpen={setMenuOpen}
          />
        </Flex>
      </>
    );
  },
);
